import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useCombobox } from 'downshift'
import { useTranslations } from 'next-intl'
import { Box, Paragraph } from '@alto-avios/alto-ui'
import { ContentfulCardLegacy } from '@reward-platform/contentful'
import {
  AutoCompleteLocationOption,
  AutoCompleteWithMobileModal,
} from '@reward-platform/lift/components'
import { UseDestinationStateReturn } from '~/components/search-and-results/SearchHotelForm/useHotelSearch'
import { createAutoCompleteLocationSource } from '~/utils/createAutoCompleteSourceFromLocation'
import RecentSearches from '../RecentSearches/RecentSearches'
import PopularDestinations from '../PopularDestinations'

import styles from './DestinationAutoComplete.module.scss'

type DestinationAutoCompleteProps = {
  showRecentSearches?: boolean
  error?: string
  // TODO: to be removed on completion of https://aviosgroup.atlassian.net/browse/RPS-14800
  popularDestinations?: ContentfulCardLegacy[]
  destination: Omit<UseDestinationStateReturn, 'fullName'>
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const DestinationAutoComplete: React.FC<DestinationAutoCompleteProps> = (props) => {
  const { destination, showRecentSearches, popularDestinations, error } = props
  const showPopularDestinations = popularDestinations && popularDestinations.length > 0
  const t = useTranslations()

  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const autocompleteItems = useMemo(
    () => createAutoCompleteLocationSource(destination.locations),
    [destination.locations]
  )

  const destinationInputRef = useRef<HTMLInputElement | null>(null)
  const shouldDisplayEmptyStateContainer = showPopularDestinations || showRecentSearches

  return isHydrated ? (
    <div className={shouldDisplayEmptyStateContainer ? styles.maxHeightUnset : undefined}>
      <AutoCompleteWithMobileModal
        ref={destinationInputRef}
        name="destination"
        label={t('common.forms.labels.destination')}
        value={destination.searchString}
        items={autocompleteItems}
        onInputFocus={destination.onInputFocus}
        onInputValueChange={(value) => {
          if (value.type === useCombobox.stateChangeTypes.InputBlur) {
            return
          }
          destination.setSearchString(value.inputValue ?? '')
        }}
        onSelectedItemChange={(value) => {
          destination.handleSuggestionClick(value.selectedItem?.value || '')
        }}
        error={error}
      >
        {!destination.isLoading && !destination.noResultsFound ? (
          autocompleteItems.map((item, index) => (
            <Box flexDirection="column" display="flex">
              <AutoCompleteLocationOption key={item.key} item={item} index={index} />
            </Box>
          ))
        ) : (
          <Box borderRadius="lg" padding="lg">
            {destination.isLoading ? (
              <Paragraph>{t('common.forms.labels.searching')}</Paragraph>
            ) : (
              <Paragraph>
                {t('common.forms.labels.no_results')}
                <b>"{destination.searchString}"</b>
              </Paragraph>
            )}
          </Box>
        )}
        {shouldDisplayEmptyStateContainer ? (
          <Box flexDirection="row" display="flex" className={styles.emptyStateContainer}>
            {showRecentSearches ? (
              <Box className={styles.emptyStateColumn}>
                <RecentSearches />
              </Box>
            ) : null}
            {showPopularDestinations ? (
              <Box className={styles.emptyStateColumn}>
                <PopularDestinations
                  destinations={popularDestinations}
                  displayInline={!showRecentSearches}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}
      </AutoCompleteWithMobileModal>
    </div>
  ) : null
}

export default DestinationAutoComplete
